/**
 * NotchesManualTradingPage — /n2/manual-trading
 *
 * Explains what Researched Opportunities are, how a customer uses them
 * for manual trading, and where to compare research plans.
 *
 * Manual trading is placed through the customer's own broker. This page
 * never proposes manual order entry. Plan-comparison links point at
 * /n2/plans — the public comparison. They never route to the
 * auth-gated /upgrade page from public marketing.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { palette, type as typeTokens } from './theme';
import { NotchesExplainerFrame, useIsMobile, useReducedMotion } from './NotchesExplainerFrame';

export const NotchesManualTradingPage: React.FC = () => {
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();

  return (
    <NotchesExplainerFrame
      breadcrumbTrail={[
        { label: 'Notches', to: '/n2' },
        { label: 'Execute', to: '/n2?path=execute' },
        { label: 'Manual Trading' },
      ]}
    >
      <Hero reduced={reduced} isMobile={isMobile} />

      <Section title="How manual trading with Researched Opportunities works">
        <p style={paragraphStyle}>
          Researched Opportunities are the trade candidates Notches
          publishes for its research plans. Each candidate arrives with
          the current underlying price, supporting analysis, risk
          considerations, and — where applicable — a proposed structure
          and range. You decide whether to place it and route it through
          your own broker.
        </p>
        <ol style={orderedListStyle}>
          <li>
            <strong>Explore</strong> — open Evaluate to browse candidates
            on stocks and equity options (Premier), indexes and ETFs
            (SPX, SPY, QQQ, IWM), or futures options (ES, MES, GC, CL).
          </li>
          <li>
            <strong>Read</strong> — review the supporting analysis, the
            analytical rating, and the risk considerations included with
            each opportunity.
          </li>
          <li>
            <strong>Decide</strong> — pick the ones that fit your account
            size, timeframe, and risk tolerance.
          </li>
          <li>
            <strong>Trade</strong> — place the order through your own
            broker. Notches does not provide manual order entry and never
            touches your broker credentials for manual trading.
          </li>
        </ol>
      </Section>

      <Section title="Membership plans">
        <p style={paragraphStyle}>
          Five research plans cover the catalog — Premier, SPX Only,
          Index &amp; ETF Research, Futures Options Research, and All
          Research. The Boring Bucket is a separate mentored membership.
          Public market research remains free.
        </p>

        <ul style={compactSummaryStyle}>
          <li><strong>Premier</strong> — $29.99/mo. Stocks, equity options, reporting.</li>
          <li><strong>SPX Only</strong> — $79.99/mo. SPX.</li>
          <li><strong>Index &amp; ETF Research</strong> — $99.99/mo. SPX, SPY, QQQ, IWM.</li>
          <li><strong>Futures Options Research</strong> — $99.99/mo. ES, MES, GC, CL.</li>
          <li><strong>All Research</strong> — $199.99/mo. Premier plus the listed index, ETF, and futures-options coverage.</li>
          <li><strong>The Boring Bucket</strong> — $299/mo. All Research plus the mentored membership.</li>
        </ul>

        <p style={{ ...paragraphStyle, marginTop: 8 }}>
          <Link to="/n2/plans" style={inlineLinkStyle}>
            Compare all plans →
          </Link>
        </p>
      </Section>

      <Section title="Where to next">
        <div style={ctaRowStyle(isMobile)}>
          <CtaLink to="/n2?path=evaluate" primary>
            Explore Researched Opportunities →
          </CtaLink>
          <CtaLink to="/n2/plans">Compare all plans →</CtaLink>
          <CtaLink to="/n2/automated-trading">
            Or: Automated Trading Engines →
          </CtaLink>
        </div>
      </Section>
    </NotchesExplainerFrame>
  );
};

// ─── Building blocks ─────────────────────────────────────────────────────

const Hero: React.FC<{ reduced: boolean; isMobile: boolean }> = ({
  reduced,
  isMobile,
}) => (
  <header
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      animation: reduced
        ? undefined
        : 'notches-explainer-fade-in 320ms ease-out both',
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 12,
        fontWeight: 700,
        color: palette.ink3,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
      }}
    >
      Execute · Manual Trading
    </span>
    <h1
      tabIndex={-1}
      style={{
        fontFamily: typeTokens.serif,
        fontSize: isMobile ? 32 : 40,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.01em',
        lineHeight: 1.15,
        textWrap: 'balance' as any,
        outline: 'none',
      }}
    >
      Use researched opportunities to inform trades you place through your
      own broker.
    </h1>
    <p
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 16,
        color: palette.ink2,
        lineHeight: 1.5,
        margin: 0,
        maxWidth: 620,
      }}
    >
      Notches researches stocks and equity options, index and ETF
      options, and futures options — and publishes candidates with
      supporting analysis and risk considerations. You keep full control:
      pick what fits, size it yourself, and route the trade through the
      broker you already use.
    </p>
  </header>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <h2
      style={{
        fontFamily: typeTokens.serif,
        fontSize: 24,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.005em',
        lineHeight: 1.2,
      }}
    >
      {title}
    </h2>
    {children}
  </section>
);

const CtaLink: React.FC<{
  to: string;
  primary?: boolean;
  children: React.ReactNode;
}> = ({ to, primary, children }) => (
  <Link
    to={to}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 16px',
      borderRadius: 2,
      textDecoration: 'none',
      fontFamily: typeTokens.sans,
      fontSize: 14,
      fontWeight: 600,
      color: primary ? palette.paper : palette.ink,
      background: primary ? palette.goldDeep : 'transparent',
      border: `1px solid ${primary ? palette.goldDeep : palette.line}`,
      transition: 'background 150ms ease-out, color 150ms ease-out',
    }}
  >
    {children}
  </Link>
);

const paragraphStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: 640,
};

const orderedListStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.65,
  margin: 0,
  paddingLeft: 22,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxWidth: 640,
};

const inlineLinkStyle: React.CSSProperties = {
  color: palette.goldDeep,
  textDecoration: 'underline',
  textDecorationColor: palette.line,
  textUnderlineOffset: 3,
  fontWeight: 600,
};

const compactSummaryStyle: React.CSSProperties = {
  listStyle: 'disc',
  paddingLeft: 22,
  margin: 0,
  fontFamily: typeTokens.sans,
  fontSize: 14,
  color: palette.ink2,
  lineHeight: 1.65,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxWidth: 640,
};

const ctaRowStyle = (isMobile: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 12,
  flexWrap: 'wrap',
});
